/**
 * MeshClient — high-level API for the mesh layer.
 *
 * Wires together Identity + MeshPacket + BloomFilter + one or more
 * BleTransports. UI modules (NearbyPanel) talk only to MeshClient; they
 * never touch transports or the wire format directly.
 *
 * Events (subscribe via on*):
 *   onPeer(peer)          — peer appeared or re-announced
 *   onPeerLost(peerId)    — peer aged out
 *   onMessage(envelope)   — a broadcast/private message arrived
 *   onStatus(status)      — transport lifecycle (starting, running, stopped)
 *
 * Sending:
 *   broadcast(text)       — send a Broadcast packet to everyone in the mesh
 *   sendPrivate(id, text) — send a Private packet (placeholder; Noise not wired yet)
 */

import { loadIdentity, setNickname as setStoredNickname } from './Identity.js';
import { BloomFilter } from './BloomFilter.js';
import {
  TYPE, FLAGS, encode, decode, packetIdOf,
  payloadToString, isBroadcastTarget, decrementTtl,
  encodePadded,
} from './MeshPacket.js';
import { encode as encodeAnnounce, decode as decodeAnnounce } from './AnnouncePacket.js';
import { SimulatedTransport } from './transports/SimulatedTransport.js';
import { WebBluetoothTransport } from './transports/WebBluetoothTransport.js';
import { CapacitorBleTransport } from './transports/CapacitorBleTransport.js';
import { CapacitorPeripheralTransport } from './transports/CapacitorPeripheralTransport.js';

export class MeshClient {
  constructor() {
    this.identity = null;
    this.transports = [];
    this.bloom = new BloomFilter();
    /** @type {Map<string, any>} */
    this.peers = new Map();
    this.listeners = {
      peer: new Set(), peerLost: new Set(), message: new Set(), status: new Set(),
    };
    this.state = 'idle';
  }

  // ---------- lifecycle ----------

  /**
   * Initialise the mesh. Generates (or loads) the local identity, wires up
   * the transports, and starts them. Idempotent.
   *
   * @param {{
   *   nickname?: string,
   *   transports?: Array<'simulated'|'web-bluetooth'>,
   *   ephemeralIdentity?: boolean, // if true OR if only the simulated
   *                                // transport is active, generate a
   *                                // per-tab identity so multiple tabs on
   *                                // the same origin can act as distinct
   *                                // peers during local smoke tests.
   * }} opts
   */
  async init(opts = {}) {
    if (this.state !== 'idle') return;
    this._setStatus('starting');

    const requested = opts.transports ?? ['simulated'];
    // Default to a per-tab identity whenever the only configured transport
    // is the dev-mode simulator. Real BLE transports (where distinct tabs
    // are distinct devices) override this to false.
    const fresh = opts.ephemeralIdentity ?? (
      requested.length === 1 && requested[0] === 'simulated'
    );

    this.identity = await loadIdentity({ nickname: opts.nickname, fresh });

    for (const name of requested) {
      const t = this._buildTransport(name);
      if (!t) continue;
      t.setHandlers({
        onPeerSeen: (peer) => this._onPeerSeen(peer),
        onPeerLost: (peerId) => this._onPeerLost(peerId),
        onPacket:   (bytes, from) => this._onPacket(bytes, from),
        // Peripheral transports fire this when a central subscribes; re-send
        // our signed announce so the new central binds our sender ID.
        onNeedAnnounce: () => { this._sendAnnounce().catch(() => {}); },
      });
      try {
        await t.start();
        this.transports.push(t);
        console.log(`[MeshClient] Transport active: ${t.capabilities.name}`);
      } catch (err) {
        console.warn(`[MeshClient] Transport '${name}' failed to start:`, err.message);
      }
    }

    if (!this.transports.length) {
      console.warn('[MeshClient] No transports available — mesh features disabled');
      this._setStatus('stopped');
      return;
    }

    this._setStatus('running');

    console.log(
      '[MeshClient] Running as', this.identity.nickname,
      '#' + this.identity.peerIdHex.slice(0, 8),
      '— transports:', this.transports.map(t => t.capabilities.name).join(', ')
    );

    // Send an immediate Announce packet so peers see us right away
    await this._sendAnnounce();
  }

  async stop() {
    this._setStatus('stopping');
    // Polite leave before shutting down each transport
    await this._sendLeave().catch(() => {});
    for (const t of this.transports) {
      try { await t.stop(); } catch (_) {}
    }
    this.transports = [];
    this.peers.clear();
    this.state = 'idle';
    this._setStatus('stopped');
  }

  // ---------- public API ----------

  /** Return the current peer list (snapshot). */
  getPeers() {
    return Array.from(this.peers.values());
  }

  /** Return the local identity (peerId, nickname, etc). */
  getSelf() {
    return this.identity ? {
      peerId: this.identity.peerId,
      peerIdHex: this.identity.peerIdHex,
      nickname: this.identity.nickname,
    } : null;
  }

  /** Update the local nickname and re-announce. */
  async setNickname(nickname) {
    if (!this.identity) throw new Error('Mesh not initialised');
    await setStoredNickname(nickname);
    this.identity.nickname = nickname;
    // Ask the transport to re-announce immediately (SimulatedTransport
    // does this on its own interval, but an Announce packet here speeds
    // things up for future transports).
    await this._sendAnnounce();
  }

  /** Send an unaddressed message to everyone in the mesh. */
  async broadcast(text) {
    const packet = this._buildPacket(TYPE.MESSAGE, null, text);
    await this._dispatch(packet, null);
  }

  /**
   * Send an addressed message to a specific peer.
   * NOTE: In this slice the payload is plaintext (TYPE.MESSAGE with a
   * recipient). Bitchat uses TYPE.NOISE_ENCRYPTED for real DMs; we'll
   * wrap the payload in a Noise XX session in a later slice.
   */
  async sendPrivate(peerId, text) {
    const packet = this._buildPacket(TYPE.MESSAGE, peerId, text);
    await this._dispatch(packet, peerId);
  }

  /**
   * Trigger a BLE scan. Picks whichever real-radio transport is active —
   * Capacitor BLE on native builds, Web Bluetooth in the browser. Must be
   * called from inside a user gesture handler.
   */
  async scanWebBluetooth() {
    const ble = this.transports.find(t =>
      t instanceof CapacitorBleTransport || t instanceof WebBluetoothTransport
    );
    if (!ble) throw new Error('No BLE transport active — reload with ?ble=1 (web) or rebuild with Capacitor (native)');
    const result = await ble.requestScan();
    // iOS Bitchat binds our 8-byte sender ID to the GATT link only when it
    // sees an announce from us with TTL=7. Without the binding, every
    // subsequent MESSAGE packet is rejected as spoofing. So fire a fresh
    // announce NOW, after the connection is established — the one we sent
    // at init() went out before this GATT link existed.
    await this._sendAnnounce();
    console.log('[MeshClient] Sent post-connect announce to bind sender ID on GATT link');
    return result;
  }

  // ---------- subscribers ----------

  onPeer(fn)       { this.listeners.peer.add(fn);     return () => this.listeners.peer.delete(fn); }
  onPeerLost(fn)   { this.listeners.peerLost.add(fn); return () => this.listeners.peerLost.delete(fn); }
  onMessage(fn)    { this.listeners.message.add(fn);  return () => this.listeners.message.delete(fn); }
  onStatus(fn)     { this.listeners.status.add(fn);   return () => this.listeners.status.delete(fn); }

  // ---------- internals ----------

  _buildTransport(name) {
    if (name === 'simulated') return new SimulatedTransport({ identity: this.identity });
    if (name === 'web-bluetooth') {
      if (!WebBluetoothTransport.isSupported()) {
        console.warn('[MeshClient] web-bluetooth requested but unavailable');
        return null;
      }
      return new WebBluetoothTransport({ identity: this.identity });
    }
    if (name === 'capacitor-ble') {
      // Capacitor.isNativePlatform() check is async; we instantiate
      // unconditionally and let start() throw if the native bridge isn't
      // present. main.js gates this so we only request 'capacitor-ble' on
      // native builds.
      return new CapacitorBleTransport({ identity: this.identity });
    }
    if (name === 'capacitor-peripheral') {
      // Peripheral (advertiser) role — Stage 3b. start() throws if the
      // native BitchatPeripheral plugin or LE peripheral support is absent,
      // so it degrades gracefully on devices/platforms that can't advertise.
      return new CapacitorPeripheralTransport({ identity: this.identity });
    }
    console.warn('[MeshClient] Unknown transport:', name);
    return null;
  }

  _buildPacket(type, recipientId, payload) {
    // payload may be a string (encoded as UTF-8) or already a Uint8Array
    // (e.g. the pre-encoded TLV blob for announces).
    let body;
    if (payload instanceof Uint8Array) body = payload;
    else if (typeof payload === 'string') body = new TextEncoder().encode(payload);
    else body = new Uint8Array(0);

    return {
      type,
      ttl: 7,
      timestamp: Date.now(),
      flags: FLAGS.NONE,
      senderId: this.identity.peerId,
      recipientId: recipientId || null,
      payload: body,
    };
  }

  async _dispatch(packet, recipientId) {
    const bytes = encode(packet);
    // Record so we don't re-process our own packet if it loops back
    this.bloom.seen(packetIdOf(packet));
    for (const t of this.transports) {
      try { await t.send(bytes, recipientId); } catch (err) {
        console.warn('[MeshClient] Transport send failed:', err.message);
      }
    }
  }

  async _sendAnnounce() {
    // TLV payload per Bitchat/Packets.swift. We omit directNeighbors for now
    // because we don't track RF topology at this layer yet.
    const payload = encodeAnnounce({
      nickname: this.identity.nickname,
      noisePublicKey:   this.identity.noisePublicKey?.length === 32
        ? this.identity.noisePublicKey : undefined,
      signingPublicKey: this.identity.signingPublicKey?.length === 32
        ? this.identity.signingPublicKey : undefined,
    });
    const packet = this._buildPacket(TYPE.ANNOUNCE, null, payload);

    // Sign the announce with our Ed25519 key so iOS binds our sender ID as
    // a direct neighbour. iOS computes the signable bytes as
    //   BinaryProtocol.encode(packet, padding: true)   with ttl=0,
    //   signature=nil, isRSR=false  (BitchatPacket.swift:78-96).
    // The PKCS#7 padding is PART of the signed input — signing the raw
    // (unpadded) form produces a mismatched signature and iOS's
    // handleAnnounce rejects us with "Ignoring unverified announce".
    if (!this.identity.legacy && this.identity.ed25519?.privateKey) {
      try {
        const signable = encodePadded({ ...packet, ttl: 0, signature: null });
        const sig = new Uint8Array(await crypto.subtle.sign(
          { name: 'Ed25519' },
          this.identity.ed25519.privateKey,
          signable
        ));
        if (sig.length === 64) {
          packet.signature = sig;
          packet.flags = (packet.flags || 0) | FLAGS.HAS_SIGNATURE;
        } else {
          console.warn('[MeshClient] Unexpected Ed25519 signature length:', sig.length);
        }
      } catch (err) {
        console.warn('[MeshClient] Announce signing failed:', err.message);
      }
    } else if (this.identity.legacy) {
      // P-256 fallback: iOS only accepts Ed25519 signatures. The announce
      // will go unsigned and iOS will receive our traffic but likely not
      // bind our sender ID. This is acceptable for dev on browsers without
      // native Ed25519 support, but interop is degraded.
      console.warn('[MeshClient] Legacy P-256 keys — announce will be unsigned; iOS may reject messages');
    }

    await this._dispatch(packet, null);
  }

  async _sendLeave() {
    const packet = this._buildPacket(TYPE.LEAVE, null, '');
    await this._dispatch(packet, null);
  }

  _onPeerSeen(peer) {
    const hex = peer.idHex;
    const existing = this.peers.get(hex);
    if (existing) {
      existing.lastSeen = peer.lastSeen;
      if (peer.nickname) existing.nickname = peer.nickname;
    } else {
      this.peers.set(hex, { ...peer });
    }
    this._emit('peer', this.peers.get(hex));
  }

  _onPeerLost(peerId) {
    const hex = this._hex(peerId);
    if (this.peers.delete(hex)) {
      this._emit('peerLost', peerId);
    }
  }

  _onPacket(bytes, _fromPeerId) {
    let pkt;
    try { pkt = decode(bytes); }
    catch (err) {
      console.warn('[MeshClient] Bad packet:', err.message);
      return;
    }

    // Dedup via bloom filter
    const id = packetIdOf(pkt);
    if (this.bloom.seen(id)) return;

    // Ignore packets we originated (in case of loopback)
    if (this._hex(pkt.senderId) === this.identity.peerIdHex) return;

    // Relay if still has TTL and addressed to someone other than us only
    // (for private) or broadcast. Capability-aware transports do their own
    // relay; this client-level relay is a belt-and-braces hop.
    if (pkt.ttl > 1) {
      const decremented = decrementTtl(pkt);
      if (decremented) {
        const relayBytes = encode(decremented);
        for (const t of this.transports) {
          if (!t.capabilities.canRelay) continue;
          t.send(relayBytes, pkt.recipientId || null).catch(() => {});
        }
      }
    }

    // Deliver locally if it's for us (broadcast or addressed-to-us)
    const forUs = isBroadcastTarget(pkt.recipientId)
      || this._hex(pkt.recipientId) === this.identity.peerIdHex;
    if (!forUs) return;

    switch (pkt.type) {
      case TYPE.ANNOUNCE: {
        const announce = decodeAnnounce(pkt.payload);
        this._onPeerSeen({
          id: pkt.senderId,
          idHex: this._hex(pkt.senderId),
          nickname: announce.nickname || 'unknown',
          lastSeen: Date.now(),
          rssi: 0,
          transport: 'packet-announce',
        });
        return;
      }
      case TYPE.LEAVE: {
        this._onPeerLost(pkt.senderId);
        return;
      }
      case TYPE.MESSAGE: {
        const envelope = {
          senderId: pkt.senderId,
          senderIdHex: this._hex(pkt.senderId),
          senderNickname: this.peers.get(this._hex(pkt.senderId))?.nickname || 'unknown',
          type: isBroadcastTarget(pkt.recipientId) ? 'broadcast' : 'private',
          text: payloadToString(pkt.payload),
          timestamp: pkt.timestamp,
        };
        this._emit('message', envelope);
        return;
      }
      case TYPE.NOISE_HANDSHAKE:
      case TYPE.NOISE_ENCRYPTED:
        // We don't yet implement Noise XX. Drop silently so the assembler
        // can move on to the next frame.
        return;
      default:
        // Unknown type — silently drop
        return;
    }
  }

  _setStatus(status) {
    this.state = status;
    this._emit('status', status);
  }

  _emit(event, data) {
    for (const fn of this.listeners[event]) {
      try { fn(data); } catch (err) { console.error('[MeshClient] Listener error:', err); }
    }
  }

  _hex(u8) {
    if (!u8) return '';
    return Array.from(u8, b => b.toString(16).padStart(2, '0')).join('');
  }
}

// Convenience singleton so the rest of the app doesn't juggle instances
let singleton = null;

/**
 * Get (and lazily create) the app-wide MeshClient instance.
 */
export function getMesh() {
  if (!singleton) singleton = new MeshClient();
  return singleton;
}

export default { MeshClient, getMesh };
